import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import introJs from 'intro.js'
import 'intro.js/introjs.css'
import './planning-tour.css'
import { UserService } from 'src/services/user.service'

export function PlanningTourComponent() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [tourInstance, setTourInstance] = useState<ReturnType<typeof introJs.tour> | null>(null)

  const { data } = useQuery({
    queryKey: ['planning-tutorial'],
    queryFn: () => UserService.getPlanningTutorialSeenAt(),
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const shouldSeeTutorial = data?.shouldLaunchPlanningTutorial

  const markTutorialAsSeen = useCallback(async () => {
    try {
      await UserService.markPlanningTutorialSeen()
      queryClient?.setQueryData(['planning-tutorial'], { shouldLaunchPlanningTutorial: false })
    } catch (error) {
      console.error('Failed to mark planning tutorial as seen:', error)
    }
  }, [queryClient])

  useEffect(() => {
    if (!shouldSeeTutorial) return

    const intro = introJs.tour()

    intro.onComplete(() => {
      markTutorialAsSeen()
    })

    intro.onExit(() => {
      markTutorialAsSeen()
    })

    intro.setOptions({
      nextLabel: t('pages.planning.tour.next-button'),
      prevLabel: t('pages.planning.tour.prev-button'),
      doneLabel: t('pages.planning.tour.done-button'),
      skipLabel: t('pages.planning.tour.skip-button'),
      exitOnOverlayClick: false,
      showProgress: true,
      showBullets: true,
      disableInteraction: true,
      scrollToElement: true,
      scrollPadding: 30,
      tooltipClass: 'planning-tour-tooltip',
      highlightClass: 'planning-tour-highlight',
      buttonClass: 'planning-tour-button',
      helperElementPadding: 10,
      steps: [
        {
          element: '[data-planning-tour="title"]',
          title: t('pages.planning.tour.step-1-title'),
          intro: t('pages.planning.tour.step-1'),
          position: 'bottom',
        },
        {
          element: '[data-planning-tour="tabs"]',
          title: t('pages.planning.tour.step-2-title'),
          intro: t('pages.planning.tour.step-2'),
          position: 'bottom',
        },
        {
          element: '[data-planning-tour="days"]',
          title: t('pages.planning.tour.step-3-title'),
          intro: t('pages.planning.tour.step-3'),
          position: 'bottom',
        },
        {
          element: '[data-planning-tour="filters"]',
          title: t('pages.planning.tour.step-4-title'),
          intro: t('pages.planning.tour.step-4'),
          position: 'bottom',
        },
        {
          element: '[data-planning-tour="other-filters"]',
          title: t('pages.planning.tour.step-5-title'),
          intro: t('pages.planning.tour.step-5'),
          position: 'bottom',
        },
      ],
    })

    setTourInstance(intro)

    return () => {
      intro.exit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSeeTutorial])

  // Start tutorial tour if necessary
  useEffect(() => {
    if (tourInstance && shouldSeeTutorial) {
      tourInstance.start()
    }
  }, [tourInstance, shouldSeeTutorial])

  return null
}
